import { useCallback, useLayoutEffect, useRef } from "react";

import { fieldOptionsResponseSchema, type FormCondition } from "../schemas";

import type { AsyncSelectPaginatedResult } from "@/components/ui/v2/async-select";
import { instance } from "@/lib/axios/instance";
import type { VacancyFieldOptionType } from "@/types/response/GetVacancyFieldOptionsResponse";

const BASE_PAGE_SIZE = 10;
const MAX_PINNED_PAGE_SIZE = 50;
const PAGE_SIZE_HEADROOM = 5;

type UseFieldOptionsFetcherParams = {
  field: string;
  clientId?: string;
  values: FormCondition["values"];
};

type OptionsRequest = {
  page: number;
  pageSize: number;
  clientId?: string;
  selectedValues?: string[];
  search?: string;
};

const requestOptions = async (
  field: string,
  { page, pageSize, clientId, selectedValues, search }: OptionsRequest,
): Promise<AsyncSelectPaginatedResult<VacancyFieldOptionType>> => {
  const response = await instance.get(
    `/api/v2/Vacancies/Fields/${encodeURIComponent(field)}/Options`,
    {
      params: {
        page,
        pageSize,
        clientId,
        ...(selectedValues?.length && { selectedValues }),
        ...(search && { search }),
      },
    },
  );

  const { data, totalRecords } = fieldOptionsResponseSchema.parse(response.data);
  return { items: data, totalCount: totalRecords };
};

type PinnedPaging = Required<Pick<OptionsRequest, "pageSize" | "selectedValues">>;

const toPinnedPaging = (values: string[]): PinnedPaging => {
  const selectedValues = values.slice(0, MAX_PINNED_PAGE_SIZE);
  const pageSize =
    selectedValues.length > BASE_PAGE_SIZE
      ? Math.min(MAX_PINNED_PAGE_SIZE, selectedValues.length + PAGE_SIZE_HEADROOM)
      : BASE_PAGE_SIZE;
  return { selectedValues, pageSize };
};

export function useFieldOptionsFetcher({ field, clientId, values }: UseFieldOptionsFetcherParams) {
  const valuesRef = useRef(values);
  const pinnedPagingRef = useRef<PinnedPaging>({ selectedValues: [], pageSize: BASE_PAGE_SIZE });

  useLayoutEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const paginatedFetcher = useCallback(
    async (query?: string, page = 1): Promise<AsyncSelectPaginatedResult<VacancyFieldOptionType>> => {
      if (!field) return { items: [], totalCount: 0 };

      if (query) {
        return requestOptions(field, { page, pageSize: BASE_PAGE_SIZE, clientId, search: query });
      }

      if (page === 1) pinnedPagingRef.current = toPinnedPaging(valuesRef.current);

      return requestOptions(field, { page, clientId, ...pinnedPagingRef.current });
    },
    [field, clientId],
  );

  const matchesFetcher = useCallback(
    (query: string, pageSize: number) =>
      requestOptions(field, { page: 1, pageSize, clientId, search: query }),
    [field, clientId],
  );

  return { paginatedFetcher, matchesFetcher };
}
